import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { PublicBookingApiService } from '../services/public-booking-api.service';
import {
  ConditionalLogicRule,
  MeetingBooksLinkResponse,
  MeetingBookingAvailabilityResponse,
  MeetingBookingDurationResponse,
  MeetingBookingFormFieldResponse,
  MeetingHolidayResponse,
  MeetingUserAvailabilityOverrideResponse,
  PublicBookingRequest,
  PublicBookingResponse,
  PublicBusyInterval,
} from '../models/meeting.model';

interface FormValues { [key: string]: string | boolean | number | null; }

/** Celda del calendario de mes inline (estilo Calendly). */
interface CalendarDay {
  date: string;      // YYYY-MM-DD
  day: number;       // 1..31
  inMonth: boolean;  // pertenece al mes mostrado
  selectable: boolean; // dentro de rango, día con disponibilidad y no bloqueado
  isToday: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'app-public-booking',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-booking.component.html',
})
export class PublicBookingComponent implements OnInit {
  slug = '';
  loading = true;
  error = '';

  link: MeetingBooksLinkResponse | null = null;
  availabilities: MeetingBookingAvailabilityResponse[] = [];
  formFields: MeetingBookingFormFieldResponse[] = [];
  holidays: MeetingHolidayResponse[] = []; // INC 7: fechas bloqueadas
  // INC 7: días no disponibles del organizador (overrides isAvailable=false)
  unavailableOverrides: MeetingUserAvailabilityOverrideResponse[] = [];
  /** INC 76: franjas ocupadas (reunión + buffer) en milisegundos epoch. */
  private busyIntervals: { start: number; end: number }[] = [];
  /** Se incrementa al recargar la ocupación: invalida la memoización de slots. */
  private busyVersion = 0;
  private readonly slotCache = new Map<string, string[]>();

  // Selecciones del usuario
  selectedDuration: MeetingBookingDurationResponse | null = null;
  selectedDate = '';
  selectedSlot: string | null = null;
  formValues: FormValues = {};

  // Datos básicos del invitado (siempre presentes — INC 38)
  inviteeName = '';
  inviteeLastName = ''; // INC 38: apellido separado en la UI (se combina en inviteeName al enviar)
  inviteeEmail = '';
  inviteePhone = '';

  // Zona horaria detectada del navegador (INC 38)
  readonly browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Calendario de mes inline (estilo Calendly)
  calendarCursor = new Date(new Date().getFullYear(), new Date().getMonth(), 1); // primer día del mes mostrado
  readonly weekdayLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D']; // semana inicia lunes

  // UX
  step: 1 | 2 | 3 = 1; // 1: date/slot, 2: form, 3: confirmación
  submitting = false;
  submitted = false;
  confirmation: PublicBookingResponse | null = null;
  /** INC 80: nombre completo de quien reservó, para la pantalla de confirmación. */
  confirmedInviteeName = '';
  /**
   * INC 80: fecha y hora ya formateadas, congeladas al confirmar.
   *
   * No se puede volver a calcular desde `selectedSlot` en el paso 3: tras reservar se
   * refresca la ocupación (INC 76) y, como el horario recién tomado pasa a estar ocupado,
   * `selectedSlot` se pone a null — la confirmación se quedaba sin fecha ni hora.
   */
  confirmedWhen = '';

  constructor(
    private route: ActivatedRoute,
    private api: PublicBookingApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    if (!this.slug) {
      this.error = 'Slug inválido';
      this.loading = false;
      return;
    }
    this.loadLink();
  }

  private loadLink(): void {
    this.api.getLink(this.slug).subscribe({
      next: (link) => {
        this.link = link;
        if (!link.isActive) {
          this.error = 'Esta página de reserva no está disponible.';
          this.loading = false;
          return;
        }
        this.loadSubResources();
      },
      error: () => {
        this.error = 'No se encontró la página de reserva.';
        this.loading = false;
      },
    });
  }

  private loadSubResources(): void {
    forkJoin({
      avail: this.api.listAvailability(this.slug).pipe(catchError(() => of([]))),
      fields: this.api.listFormFields(this.slug).pipe(catchError(() => of([]))),
      holidays: this.api.listHolidays(this.slug).pipe(catchError(() => of([]))),
      // INC 7: overrides no disponibles; 400 si la link está inactiva → lista vacía
      overrides: this.api
        .listAvailabilityOverrides(this.slug)
        .pipe(catchError(() => of([] as MeetingUserAvailabilityOverrideResponse[]))),
    }).subscribe({
      next: ({ avail, fields, holidays, overrides }) => {
        this.availabilities = avail;
        this.holidays = holidays;
        // solo días no disponibles (defensivo: el backend ya devuelve solo bloqueados)
        this.unavailableOverrides = overrides.filter((o) => o.isAvailable === false);
        this.formFields = [...fields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

        // La duración sale únicamente de "Duración por defecto (min)" del link
        // (General → Slots y buffers). Ya no hay catálogo de duraciones por página.
        this.selectedDuration = this.buildFallbackDuration();

        // Inicializar form values
        for (const f of this.formFields) {
          this.formValues[f.fieldName] = f.fieldType === 'checkbox' ? false : '';
        }

        // Abrir el calendario en el mes de la primera fecha reservable
        const [cy, cm] = this.minBookDate.split('-').map(Number);
        this.calendarCursor = new Date(cy, cm - 1, 1);

        this.loadBusy(); // INC 76: horarios ya ocupados (con su buffer)
        this.loading = false;
      },
    });
  }

  // ─────────── Lógica de slots ───────────

  /**
   * Duración de la reunión, tomada de defaultDurationMinutes del link.
   * Se envuelve en el mismo shape que usaba el catálogo de duraciones para no
   * tocar el resto del flujo (slots, resumen y payload de reserva), y garantiza
   * que selectedDuration nunca sea null (INC 59: si lo era, el botón
   * "Continuar" quedaba deshabilitado aun con fecha y hora elegidas).
   */
  private buildFallbackDuration(): MeetingBookingDurationResponse {
    const minutes = this.link?.defaultDurationMinutes || 30;
    return {
      id: 0,
      idBookingLink: this.link?.id ?? 0,
      durationMinutes: minutes,
      label: `${minutes} min`,
    };
  }

  selectDate(date: string): void {
    this.selectedDate = date;
    this.selectedSlot = null;
  }

  /** INC 7: feriado que bloquea una fecha (o null). */
  holidayForDate(date: string): MeetingHolidayResponse | null {
    if (!date) return null;
    const monthDay = date.slice(5); // MM-DD para recurrentes
    return this.holidays.find(h =>
      h.isRecurring
        ? (h.holidayDate || '').slice(5) === monthDay
        : h.holidayDate === date
    ) || null;
  }

  /** INC 7: override "no disponible" que bloquea una fecha (o null). */
  overrideForDate(date: string): MeetingUserAvailabilityOverrideResponse | null {
    if (!date) return null;
    return this.unavailableOverrides.find((o) => o.overrideDate === date) || null;
  }

  /** INC 7: true si la fecha está bloqueada por feriado u override. */
  isDateBlocked(date: string): boolean {
    return !!this.holidayForDate(date) || !!this.overrideForDate(date);
  }

  /** Feriado que bloquea la fecha seleccionada (o null). */
  get selectedDateHoliday(): MeetingHolidayResponse | null {
    return this.holidayForDate(this.selectedDate);
  }

  /** Override que bloquea la fecha seleccionada (o null). */
  get selectedDateOverride(): MeetingUserAvailabilityOverrideResponse | null {
    return this.overrideForDate(this.selectedDate);
  }

  /** true si la fecha seleccionada está bloqueada por feriado u override. */
  get selectedDateBlocked(): boolean {
    return this.isDateBlocked(this.selectedDate);
  }

  /** Slots disponibles para una fecha cualquiera (respeta bloqueos, rango y ocupación). */
  computeSlots(date: string): string[] {
    if (!date || !this.link) return [];
    const isToday = date === this.toIsoDate(new Date());
    const durationMin = this.selectedDuration?.durationMinutes || this.link.defaultDurationMinutes || 30;
    // El cálculo se repite en cada ciclo de detección de cambios (calendario de 42
    // celdas + lista del día), así que se memoiza por fecha/duración/versión de
    // ocupación. HOY no se cachea: su umbral depende de la hora actual.
    const cacheKey = `${date}|${durationMin}|${this.busyVersion}`;
    if (!isToday) {
      const cached = this.slotCache.get(cacheKey);
      if (cached) return cached;
    }

    if (this.isDateBlocked(date)) return []; // INC 7: día bloqueado (feriado u override)
    const d = new Date(date + 'T00:00:00');
    const dayOfWeek = d.getDay();
    const windows = this.availabilities.filter((a) => a.dayOfWeek === dayOfWeek);
    if (windows.length === 0) return [];

    const intervalMin = this.link.slotIntervalMinutes || durationMin;
    const slots: string[] = [];

    // INC 60: si el día elegido es HOY, ocultar los horarios cuya hora ya pasó
    // (respecto a la hora actual del navegador) y respetar el aviso mínimo. Para
    // días futuros no se aplica umbral. minSlotStart en minutos-del-día; -1 = sin umbral.
    let minSlotStart = -1;
    if (isToday) {
      const now = new Date();
      const notice = this.link.minNoticeMinutes || 0;
      minSlotStart = now.getHours() * 60 + now.getMinutes() + notice;
    }

    // INC 76: offset de la zona del invitado UNA vez por fecha (crear un
    // Intl.DateTimeFormat por slot era demasiado caro para un getter de plantilla).
    const offset = this.offsetForZone(date, '12:00', this.browserTimezone);

    for (const w of windows) {
      const startMin = this.timeToMinutes(w.startTime);
      const endMin = this.timeToMinutes(w.endTime);
      for (let t = startMin; t + durationMin <= endMin; t += intervalMin) {
        if (t < minSlotStart) continue; // INC 60: slot en el pasado (o antes del aviso mínimo)
        const time = this.minutesToTime(t);
        if (this.isBusy(date, time, offset, durationMin)) continue; // INC 76
        slots.push(time);
      }
    }
    if (!isToday) this.slotCache.set(cacheKey, slots);
    return slots;
  }

  /**
   * INC 76 — ¿el horario choca con una franja ocupada del organizador?
   *
   * <p>Las franjas vienen del backend ya expandidas con el buffer de la página, así
   * que aquí basta con el test de solapamiento. Antes la página ofrecía TODOS los
   * horarios de la disponibilidad semanal y el visitante solo descubría el choque al
   * enviar la reserva ("El horario solicitado no respeta el tiempo de margen (buffer)
   * entre reuniones del organizador").</p>
   */
  private isBusy(date: string, time: string, offset: string, durationMin: number): boolean {
    if (!this.busyIntervals.length) return false;
    const start = Date.parse(`${date}T${time}:00${offset}`);
    if (isNaN(start)) return false; // ante la duda, se ofrece: el backend sigue validando
    const end = start + durationMin * 60000;
    return this.busyIntervals.some((b) => b.start < end && b.end > start);
  }

  /**
   * INC 76 — carga las franjas ocupadas de todo el horizonte reservable de la página.
   * Se vuelve a pedir tras cada reserva para que una segunda reserva en la misma
   * sesión no vuelva a ofrecer el horario recién tomado.
   */
  private loadBusy(): void {
    if (!this.link) return;
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setDate(to.getDate() + (this.link.maxAdvanceDays || 60) + 1);
    this.api
      .listBusy(this.slug, from.toISOString(), to.toISOString())
      .pipe(catchError(() => of([] as PublicBusyInterval[])))
      .subscribe((list) => {
        this.busyIntervals = list
          .map((b) => ({ start: Date.parse(b.start), end: Date.parse(b.end) }))
          .filter((b) => !isNaN(b.start) && !isNaN(b.end));
        this.busyVersion++;
        this.slotCache.clear();
        // El horario elegido pudo quedar ocupado mientras el visitante llenaba el form.
        if (this.selectedSlot && !this.slotsForSelectedDate.includes(this.selectedSlot)) {
          this.selectedSlot = null;
        }
      });
  }

  /** Slots para el día seleccionado */
  get slotsForSelectedDate(): string[] {
    return this.computeSlots(this.selectedDate);
  }

  // ─────────── Calendario de mes inline ───────────

  /** Etiqueta "junio 2026" del mes mostrado. */
  get calendarMonthLabel(): string {
    return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(this.calendarCursor);
  }

  /** Etiqueta "lunes, 8 de junio" de la fecha seleccionada. */
  get selectedDateLabel(): string {
    if (!this.selectedDate) return '';
    const d = new Date(this.selectedDate + 'T00:00:00');
    return new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  }

  /** Matriz de semanas (lunes→domingo) del mes mostrado. */
  get calendarWeeks(): CalendarDay[][] {
    const year = this.calendarCursor.getFullYear();
    const month = this.calendarCursor.getMonth();
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // semana inicia lunes
    const todayStr = this.toIsoDate(new Date());
    const min = this.minBookDate;
    const max = this.maxBookDate;
    const weeks: CalendarDay[][] = [];

    for (let w = 0; w < 6; w++) {
      const week: CalendarDay[] = [];
      for (let i = 0; i < 7; i++) {
        const cur = new Date(year, month, 1 - startOffset + w * 7 + i);
        const date = this.toIsoDate(cur);
        const inMonth = cur.getMonth() === month;
        const inRange = date >= min && date <= max;
        week.push({
          date,
          day: cur.getDate(),
          inMonth,
          selectable: inMonth && inRange && !this.isDateBlocked(date) && this.computeSlots(date).length > 0,
          isToday: date === todayStr,
          isSelected: date === this.selectedDate,
        });
      }
      weeks.push(week);
    }
    // Omitir una 6ª fila que cae íntegramente fuera del mes
    return weeks.filter((week) => week.some((c) => c.inMonth));
  }

  get canGoPrevMonth(): boolean {
    const lastDayPrev = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth(), 0);
    return this.toIsoDate(lastDayPrev) >= this.minBookDate;
  }

  get canGoNextMonth(): boolean {
    const firstDayNext = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth() + 1, 1);
    return this.toIsoDate(firstDayNext) <= this.maxBookDate;
  }

  prevMonth(): void {
    if (!this.canGoPrevMonth) return;
    this.calendarCursor = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth() - 1, 1);
  }

  nextMonth(): void {
    if (!this.canGoNextMonth) return;
    this.calendarCursor = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth() + 1, 1);
  }

  private timeToMinutes(v: any): number {
    if (!v) return 0;
    if (typeof v === 'string') {
      const [h, m] = v.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    }
    return (v.hour || 0) * 60 + (v.minute || 0);
  }

  private minutesToTime(m: number): string {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }

  get minBookDate(): string {
    const d = new Date();
    const notice = this.link?.minNoticeMinutes || 0;
    d.setMinutes(d.getMinutes() + notice);
    return this.toIsoDate(d);
  }

  get maxBookDate(): string {
    const d = new Date();
    d.setDate(d.getDate() + (this.link?.maxAdvanceDays || 60));
    return this.toIsoDate(d);
  }

  private toIsoDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  selectSlot(slot: string): void {
    this.selectedSlot = slot;
  }

  goToForm(): void {
    if (!this.selectedDate || !this.selectedSlot || !this.selectedDuration) return;
    this.step = 2;
  }

  backToSlots(): void {
    this.step = 1;
  }

  // ─────────── INC 38: lógica condicional de visibilidad ───────────

  /** Parsea el JSON de `conditionalLogic` a una regla, o null si no hay/está mal formado. */
  private parseConditional(raw?: string): ConditionalLogicRule | null {
    if (!raw || !raw.trim()) return null;
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === 'object' && obj.field && obj.operator) {
        return {
          field: String(obj.field),
          operator: obj.operator,
          value: obj.value != null ? String(obj.value) : '',
        };
      }
    } catch {
      // formato legado/invalido → sin regla (campo siempre visible)
    }
    return null;
  }

  /**
   * INC 38 — evalúa si un campo debe mostrarse según su `conditionalLogic`.
   * Sin regla (null/vacío) → siempre visible (retrocompatibilidad).
   */
  isFieldVisible(field: MeetingBookingFormFieldResponse): boolean {
    const rule = this.parseConditional(field.conditionalLogic);
    if (!rule) return true;

    const current = this.formValues[rule.field];
    const actual = current == null ? '' : String(current);
    const expected = rule.value ?? '';

    switch (rule.operator) {
      case 'equals':
        return actual === expected;
      case 'notEquals':
        return actual !== expected;
      case 'contains':
        return actual.toLowerCase().includes(expected.toLowerCase());
      default:
        return true;
    }
  }

  /** Campos visibles (no ocultos) según la lógica condicional. */
  get visibleFormFields(): MeetingBookingFormFieldResponse[] {
    return this.formFields.filter((f) => this.isFieldVisible(f));
  }

  // ─────────── Submit ───────────

  get inviteeEmailValid(): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.inviteeEmail.trim());
  }

  canSubmit(): boolean {
    if (!this.selectedDate || !this.selectedSlot || !this.selectedDuration || !this.link) return false;
    if (!this.inviteeName.trim() || !this.inviteeEmailValid) return false;
    if (this.link.gdprConsentEnabled && this.formValues['_gdpr'] !== true) return false;
    for (const f of this.formFields) {
      // INC 38: un campo oculto por la lógica condicional no es requerido
      if (f.isRequired && this.isFieldVisible(f)) {
        const v = this.formValues[f.fieldName];
        if (v === '' || v === null || v === undefined || v === false) return false;
      }
    }
    return true;
  }

  submit(): void {
    if (!this.canSubmit() || !this.link) return;

    const prequalData: Record<string, unknown> = {};
    for (const f of this.formFields) {
      // INC 38: no enviar el valor de un campo oculto por la lógica condicional
      if (!this.isFieldVisible(f)) continue;
      prequalData[f.fieldName] = this.formValues[f.fieldName];
    }

    // INC 38: combinar nombre + apellido en inviteeName (el backend solo acepta un campo)
    const fullName = [this.inviteeName.trim(), this.inviteeLastName.trim()]
      .filter(Boolean)
      .join(' ');

    const body: PublicBookingRequest = {
      datetimeStart: this.buildLocalIso(this.selectedDate, this.selectedSlot!),
      durationMinutes: this.selectedDuration!.durationMinutes,
      inviteeName: fullName,
      inviteeEmail: this.inviteeEmail.trim(),
      inviteePhone: this.inviteePhone.trim() || undefined,
      inviteeTimezone: this.browserTimezone,
      preQualificationData: this.formFields.length > 0 ? JSON.stringify(prequalData) : undefined,
    };

    this.submitting = true;
    this.error = '';
    this.api.book(this.slug, body).subscribe({
      next: (confirmation) => {
        this.submitting = false;
        this.submitted = true;
        this.confirmation = confirmation;
        // INC 80: la confirmación saluda a quien acaba de reservar y conserva el horario
        // ANTES de que loadBusy() invalide el slot recién tomado.
        this.confirmedInviteeName = fullName;
        this.confirmedWhen = this.formattedSelected();
        this.step = 3;
        this.loadBusy(); // INC 76: el horario recién tomado deja de ofrecerse
      },
      error: (err) => {
        this.submitting = false;
        this.error = err.error?.message || 'No se pudo agendar la reunión. Inténtalo de nuevo.';
        this.toast.error({ title: 'Error', message: this.error });
        // INC 76: si el choque lo detectó el servidor (alguien reservó mientras tanto),
        // refrescar la ocupación para que el horario desaparezca de la lista.
        this.loadBusy();
      },
    });
  }

  private buildLocalIso(date: string, time: string): string {
    // INC 18: el slot que el usuario eligió es hora de pared en SU zona horaria
    // (inviteeTimezone = browserTimezone). toISOString() lo convertía a UTC usando
    // la TZ del SO del navegador y desfasaba el horario. Construimos un OffsetDateTime
    // con el offset EXPLÍCITO de la zona del invitado para ese instante, de modo que
    // el backend (OffsetDateTime) reciba exactamente la hora que el usuario ve.
    const offset = this.offsetForZone(date, time, this.browserTimezone);
    return `${date}T${time}:00${offset}`;
  }

  /** Offset (+HH:mm / -HH:mm / Z) de una zona IANA para una fecha/hora de pared dada. */
  private offsetForZone(date: string, time: string, timeZone: string): string {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    try {
      // Instante de referencia tratando la hora de pared como UTC
      const asUtc = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
      // Cómo se ve ese instante en la zona destino → diferencia = offset
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
      const parts = dtf.formatToParts(new Date(asUtc));
      const get = (t: string) => Number(parts.find((p) => p.type === t)?.value || '0');
      let hour = get('hour');
      if (hour === 24) hour = 0; // algunos motores devuelven 24 a medianoche
      const asZoned = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
      const offsetMinutes = Math.round((asZoned - asUtc) / 60000);
      if (offsetMinutes === 0) return 'Z';
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const abs = Math.abs(offsetMinutes);
      const oh = String(Math.floor(abs / 60)).padStart(2, '0');
      const om = String(abs % 60).padStart(2, '0');
      return `${sign}${oh}:${om}`;
    } catch {
      // Fallback: offset local del navegador
      const local = new Date(y, m - 1, d, hh, mm, 0, 0);
      const offsetMinutes = -local.getTimezoneOffset();
      if (offsetMinutes === 0) return 'Z';
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const abs = Math.abs(offsetMinutes);
      const oh = String(Math.floor(abs / 60)).padStart(2, '0');
      const om = String(abs % 60).padStart(2, '0');
      return `${sign}${oh}:${om}`;
    }
  }

  parseOptions(raw?: string): string[] {
    if (!raw) return [];
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed);
        return Array.isArray(arr) ? arr.map(String) : [];
      } catch {
        return [];
      }
    }
    return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  }

  /** Etiqueta visible del canal de la reunión (videollamada o presencial). */
  get channelLabel(): string | null {
    if (!this.link) return null;
    const p = this.link.videoProvider;
    if (p) {
      const map: Record<string, string> = {
        jitsi: 'Videollamada (Jitsi)',
        google: 'Google Meet',
        microsoft: 'Microsoft Teams',
        zoom: 'Zoom',
        custom: 'Videollamada',
      };
      return map[p] || 'Videollamada';
    }
    if (this.link.physicalAddress?.trim()) return 'Presencial';
    return null;
  }

  formattedSelected(): string {
    if (!this.selectedDate || !this.selectedSlot) return '';
    try {
      const d = new Date(this.selectedDate + 'T' + this.selectedSlot + ':00');
      return new Intl.DateTimeFormat('es-ES', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(d);
    } catch { return ''; }
  }
}
